import { createRoot } from 'react-dom/client';
import App from './App.tsx';
// Initialises i18next and applies the stored language to <html lang> before the first render,
// so the app never paints an English frame and then swaps it for Telugu.
import './i18n';
import './index.css';
import 'shepherd.js/dist/css/shepherd.css';

// Suppress debug logs in production to prevent PII leakage in browser console
if (import.meta.env.PROD) {
  const noop = () => {};
  console.log = noop;
  console.info = noop;
  console.debug = noop;
  // console.error and console.warn are intentionally kept for runtime error visibility
}

// Recover from a stale bundle after a deploy.
//
// Routes are lazy-loaded, so a tab that was open when a deploy landed still holds the OLD
// index.html and asks for chunk filenames that the deploy has already pruned. CloudFront rewrites
// the resulting 404 to index.html (it must, for client-side routing), so the browser receives HTML
// where it expected a module and refuses it:
//
//   Failed to load module script: Expected a JavaScript-or-Wasm module script but the server
//   responded with a MIME type of "text/html"
//
// Nothing about that is recoverable in place — the chunk genuinely no longer exists. What the user
// needs is the new index.html, which one reload provides. Vite fires `vite:preloadError` for
// exactly this case.
//
// The reload is guarded by a sessionStorage flag so a genuinely broken deploy cannot put the tab
// into an endless refresh loop; the second failure is allowed through to the ErrorBoundary, where
// it is at least visible.
const RELOAD_GUARD = 'clinvea_chunk_reload_attempted';

window.addEventListener('vite:preloadError', (event) => {
  if (sessionStorage.getItem(RELOAD_GUARD)) {
    // Already tried once. Let it surface rather than reloading forever.
    return;
  }
  try {
    sessionStorage.setItem(RELOAD_GUARD, '1');
  } catch {
    // Private mode or blocked storage — reloading once unguarded is still better than a dead route.
  }
  event.preventDefault();
  window.location.reload();
});

// Clear the guard once the app has mounted successfully, so a LATER deploy in the same tab can
// recover the same way. Without this, one recovery would disarm the mechanism for the session.
window.addEventListener('load', () => {
  try {
    sessionStorage.removeItem(RELOAD_GUARD);
  } catch {
    /* ignore */
  }
});

createRoot(document.getElementById('root')!).render(
  <App />
);
